import { z } from 'zod';
import { StateError } from '../errors';
import type { CornerName } from './axial';
import { validateSeaMap, WATER_POOL_KEY } from './seaSchema';

// Map JSON schema (plan §6.7), zod + semantic validation. A custom map is just this JSON
// (H4's paste-a-map field feeds it through the same validator, T-NET-019). Setup-viability
// (the §13.4 auto-policy completing a full setup) joins validateMapDef when autoPolicy
// lands in H1-S12 — T-MAP-008's structural rejects are enforced from S1.

export const HARBOR_DIRS = ['NE', 'E', 'SE', 'SW', 'W', 'NW'] as const;
export type HarborDir = (typeof HARBOR_DIRS)[number];

export interface MapHexJson {
  q: number;
  r: number;
  tile: string | 'random';
  /** 2–6 | 8–12, null (never produces), or 'auto' (disc policy assigns). */
  disc: number | null | 'auto';
  /** H20-S1 (sea maps only): the dealing region whose pools this cell draws from. */
  region?: string;
}

export interface MapHarborJson {
  q: number;
  r: number;
  dir: HarborDir;
  kind: '3:1' | string; // '3:1' or a resource id (2:1)
  /** H20-S1 (sea maps only): harbour kinds shuffle WITHIN a region — the per-island deal. */
  region?: string;
}

export interface MapCellRef {
  q: number;
  r: number;
}

/** A fixed water cell; `frame` marks the outer frame ring (water the pirate may use, rarely buildable). */
export interface MapWaterJson {
  q: number;
  r: number;
  frame?: true;
}

/** Rule numbers a scenario carries ON THE MAP, so they ride the logged config (plan §4.12). */
export interface MapSeaRules {
  goldYield: { settlement: number; city: number };
  discoveryReward: number;
  shipMovesPerTurn: number;
  setupGoldPerHex: number;
}

/** Each footprint's own layout rules (the reference differs per scenario — plan §5). */
export interface MapSeaConstraints {
  /** No two red discs on adjacent static land. */
  redSpacing: boolean;
  /** Tiles that may never hold a red disc (e.g. gold). */
  forbidRedOn: string[];
  /** These tiles never take these numbers (e.g. forest/pasture never 2 or 12). */
  poorNumbers: { tiles: string[]; numbers: number[] } | null;
}

/**
 * H20-S1: the sea block. ABSENT means classic semantics — and a byte-identical ResolvedMap.
 * `hexes` keep listing the land (and randomly dealt) cells; water and fog are explicit here.
 */
export interface MapSeaJson {
  waters: MapWaterJson[];
  /** frame|frame edges this footprint allows ships on (usually none). */
  frameEdges: Array<{ q: number; r: number; dir: HarborDir }>;
  fog: { cells: MapCellRef[]; pool: { tiles: Record<string, number>; discs: number[] } } | null;
  /** One anchor cell per island setup may use; null = any island. */
  setupIslands: MapCellRef[] | null;
  /**
   * Fog within reach of a setup piece is refused (a discovery during setup is engine-legal but
   * no shipped footprint allows it — Q21). A fixture that exercises that path opts in here.
   */
  setupDiscovery?: true;
  /** VP for a first settlement on a foreign island; 0 = no Landfall. */
  landfallVp: number;
  /** "the hex holding the 12" is `{ disc: 12 }`. */
  banditStart: 'desert' | { disc: number } | MapCellRef;
  /** A FIXED water cell; null ⇔ no pirate is composed. */
  pirateStart: MapCellRef | null;
  rules: MapSeaRules;
  constraints: MapSeaConstraints;
}

/** The reserved pool key a sea map may deal INSTEAD of a tile: the cell becomes open water. */
export { WATER_POOL_KEY } from './seaSchema';

export interface MapFixedPieceJson {
  owner: 'neutral';
  piece: string;
  vertex?: { q: number; r: number; c: CornerName };
  edge?: { q: number; r: number; dir: HarborDir };
}

export interface MapOverridesJson {
  supplyStock?: number;
  discardOver?: number;
  victoryTarget?: number;
  piecePools?: Record<string, number>;
}

export interface MapDefJson {
  schemaVersion: 1;
  name: string;
  minSeats: number;
  maxSeats: number;
  hexes: MapHexJson[];
  discPolicy: 'fixed' | 'spiral' | 'random';
  randomPools: {
    tiles?: Record<string, number>;
    discs?: number[];
    /** H20-S1 (sea maps only): per-region terrain (may include `water`) and disc pools. */
    regions?: Record<string, { tiles: Record<string, number>; discs: number[] }>;
  } | null;
  harbors: MapHarborJson[];
  fixedPieces: MapFixedPieceJson[] | null;
  redSpacing: boolean;
  overrides: MapOverridesJson | null;
  /** H20-S1: absent on every classic map. */
  sea?: MapSeaJson;
}

const discSchema = z.union([z.literal('auto'), z.null(), z.number().int().min(2).max(12).refine((n) => n !== 7, '7 has no disc')]);

const cellSchema = z.object({ q: z.number().int().min(-64).max(64), r: z.number().int().min(-64).max(64) });
const seaSchema = z.object({
  waters: z.array(z.object({ q: z.number().int().min(-64).max(64), r: z.number().int().min(-64).max(64), frame: z.literal(true).optional() })),
  frameEdges: z.array(z.object({ q: z.number().int(), r: z.number().int(), dir: z.enum(HARBOR_DIRS) })),
  fog: z
    .object({
      cells: z.array(cellSchema).min(1),
      pool: z.object({ tiles: z.record(z.number().int().min(0)), discs: z.array(z.number().int().min(2).max(12)) }),
    })
    .nullable(),
  setupIslands: z.array(cellSchema).min(1).nullable(),
  setupDiscovery: z.literal(true).optional(),
  landfallVp: z.number().int().min(0).max(10),
  banditStart: z.union([z.literal('desert'), z.object({ disc: z.number().int().min(2).max(12) }), cellSchema]),
  pirateStart: cellSchema.nullable(),
  rules: z.object({
    goldYield: z.object({ settlement: z.number().int().min(0).max(4), city: z.number().int().min(0).max(8) }),
    discoveryReward: z.number().int().min(0).max(4),
    shipMovesPerTurn: z.number().int().min(0).max(4),
    setupGoldPerHex: z.number().int().min(0).max(4),
  }),
  constraints: z.object({
    redSpacing: z.boolean(),
    forbidRedOn: z.array(z.string().min(1)),
    poorNumbers: z.object({ tiles: z.array(z.string().min(1)), numbers: z.array(z.number().int().min(2).max(12)) }).nullable(),
  }),
});

export const mapDefSchema = z.object({
  schemaVersion: z.literal(1),
  name: z.string().min(1).max(40),
  minSeats: z.number().int().min(2).max(8),
  maxSeats: z.number().int().min(2).max(8),
  hexes: z
    .array(
      z.object({
        q: z.number().int().min(-64).max(64),
        r: z.number().int().min(-64).max(64),
        tile: z.string().min(1),
        disc: discSchema,
        region: z.string().min(1).max(24).optional(),
      }),
    )
    .min(1),
  discPolicy: z.enum(['fixed', 'spiral', 'random']),
  randomPools: z
    .object({
      tiles: z.record(z.number().int().min(0)).optional(),
      discs: z.array(z.number().int()).optional(),
      regions: z.record(z.object({ tiles: z.record(z.number().int().min(0)), discs: z.array(z.number().int().min(2).max(12)) })).optional(),
    })
    .nullable(),
  harbors: z.array(z.object({ q: z.number().int(), r: z.number().int(), dir: z.enum(HARBOR_DIRS), kind: z.string().min(1), region: z.string().min(1).max(24).optional() })),
  fixedPieces: z
    .array(
      z.object({
        owner: z.literal('neutral'), // v0: neutral only (plan §6.7)
        piece: z.string().min(1),
        vertex: z.object({ q: z.number().int(), r: z.number().int(), c: z.enum(['N', 'S']) }).optional(),
        edge: z.object({ q: z.number().int(), r: z.number().int(), dir: z.enum(HARBOR_DIRS) }).optional(),
      }),
    )
    .nullable(),
  redSpacing: z.boolean(),
  overrides: z
    .object({
      supplyStock: z.number().int().min(1).max(200).optional(),
      discardOver: z.number().int().min(1).max(50).optional(),
      victoryTarget: z.number().int().min(3).max(50).optional(),
      piecePools: z.record(z.number().int().min(1).max(60)).optional(),
    })
    .nullable(),
  sea: seaSchema.optional(),
});

export interface MapValidationEnv {
  /** Known tile ids, what they produce, and whether they take a disc anyway (from the GameDef). */
  tiles: ReadonlyMap<string, { produces: string | null; numbered?: boolean }>;
  resourceIds: ReadonlySet<string>;
  pieceIds: ReadonlySet<string>;
  maxHexes: number;
  /**
   * H20-S1: may a map carry a sea block under this def? Only a def that composes the sea
   * modules can PLAY one — a classic def would ignore every ship and strand every island —
   * so the paste path (validated against the classic def) refuses it here.
   */
  acceptsSea?: boolean;
}

/** Does this tile take a disc? (the map-validation view of `carriesDisc`) */
function takesDisc(tile: { produces: string | null; numbered?: boolean }): boolean {
  return tile.produces !== null || tile.numbered === true;
}

/**
 * Shape (zod) + semantic validation (plan §6.7): known tiles; disc only on producing tiles;
 * harbors on coastal edges with a land anchor; unique hex coords; seat range sane; size caps;
 * pools cover the cells they must fill; fixedPieces reference existing sites.
 * Throws z.ZodError (shape) or StateError with a specific reason (semantics).
 * (Setup-viability — the §13.4 auto-policy completing a full setup — joins here in H1-S12.)
 */
export function validateMapDef(json: unknown, env: MapValidationEnv): MapDefJson {
  const def = mapDefSchema.parse(json) as MapDefJson;

  if (def.hexes.length > env.maxHexes) {
    throw new StateError(`map rejected: ${def.hexes.length} hexes exceeds the ${env.maxHexes}-hex size cap`);
  }
  if (def.minSeats > def.maxSeats) {
    throw new StateError(`map rejected: minSeats ${def.minSeats} above maxSeats ${def.maxSeats}`);
  }

  const cellKeys = new Set<string>();
  let randomCells = 0;
  for (const h of def.hexes) {
    const key = `${h.q},${h.r}`;
    if (cellKeys.has(key)) throw new StateError(`map rejected: duplicate hex coordinates ${key}`);
    cellKeys.add(key);
    if (h.tile === 'random') {
      randomCells += 1;
      continue;
    }
    const tile = env.tiles.get(h.tile);
    if (!tile) throw new StateError(`map rejected: unknown tile '${h.tile}'`);
    if (typeof h.disc === 'number' && !takesDisc(tile)) {
      throw new StateError(`map rejected: a disc on the non-producing tile '${h.tile}' (desert-class tiles never hold discs)`);
    }
    if (def.discPolicy === 'fixed' && takesDisc(tile) && h.disc === null) {
      throw new StateError(`map rejected: missing disc on producing tile '${h.tile}' at ${key} under fixed disc policy`);
    }
    if (def.discPolicy === 'fixed' && h.disc === 'auto') {
      throw new StateError(`map rejected: 'auto' disc at ${key} under fixed disc policy`);
    }
  }

  // H20-S1: the sea block (and everything only it may use) is validated on its own terms
  if (def.sea !== undefined) {
    if (env.acceptsSea !== true) throw new StateError('map rejected: a sea block needs a game that composes the sea modules');
    validateSeaMap(def, env, cellKeys);
    return def;
  }
  if (def.randomPools?.regions !== undefined || def.hexes.some((h) => h.region !== undefined) || def.harbors.some((h) => h.region !== undefined)) {
    throw new StateError('map rejected: dealing regions belong to a sea block');
  }
  if (def.randomPools?.tiles && Object.prototype.hasOwnProperty.call(def.randomPools.tiles, WATER_POOL_KEY)) {
    throw new StateError(`map rejected: the reserved '${WATER_POOL_KEY}' pool key belongs to a sea block`);
  }

  if (randomCells > 0) {
    const pool = def.randomPools?.tiles;
    if (!pool) throw new StateError(`map rejected: ${randomCells} random cells but no tile pool`);
    let poolTotal = 0;
    for (const [tileId, count] of Object.entries(pool)) {
      if (!env.tiles.has(tileId)) throw new StateError(`map rejected: unknown tile '${tileId}' in the random pool`);
      poolTotal += count;
    }
    if (poolTotal !== randomCells) {
      throw new StateError(`map rejected: tile pool holds ${poolTotal} tiles for ${randomCells} random cells`);
    }
  }

  // harbors: known kind; land anchor; the faced neighbor must be OFF the map (coastal edge)
  const dirDelta: Record<HarborDir, { q: number; r: number }> = {
    E: { q: 1, r: 0 },
    NE: { q: 1, r: -1 },
    NW: { q: 0, r: -1 },
    W: { q: -1, r: 0 },
    SW: { q: -1, r: 1 },
    SE: { q: 0, r: 1 },
  };
  for (const h of def.harbors) {
    if (h.kind !== '3:1' && !env.resourceIds.has(h.kind)) {
      throw new StateError(`map rejected: harbor kind '${h.kind}' is neither 3:1 nor a known resource`);
    }
    if (!cellKeys.has(`${h.q},${h.r}`)) {
      throw new StateError(`map rejected: harbor anchor h${h.q},${h.r} is not a map cell`);
    }
    const d = dirDelta[h.dir];
    if (cellKeys.has(`${h.q + d.q},${h.r + d.r}`)) {
      throw new StateError(`map rejected: harbor at h${h.q},${h.r} ${h.dir} faces a land hex — harbors sit on coastal edges only`);
    }
  }

  for (const p of def.fixedPieces ?? []) {
    if (!env.pieceIds.has(p.piece)) throw new StateError(`map rejected: fixed piece '${p.piece}' is not a known piece id`);
    if (!p.vertex && !p.edge) throw new StateError(`map rejected: a fixed piece needs a vertex or edge site`);
    const site = p.vertex ?? p.edge!;
    // the site must touch the map: at least one of the (up to 3) hexes around it is a cell
    const touching =
      p.vertex !== undefined
        ? p.vertex.c === 'N'
          ? [
              { q: site.q, r: site.r },
              { q: site.q, r: site.r - 1 },
              { q: site.q + 1, r: site.r - 1 },
            ]
          : [
              { q: site.q, r: site.r },
              { q: site.q, r: site.r + 1 },
              { q: site.q - 1, r: site.r + 1 },
            ]
        : [{ q: site.q, r: site.r }];
    if (!touching.some((c) => cellKeys.has(`${c.q},${c.r}`))) {
      throw new StateError(`map rejected: fixed piece site off the map at ${site.q},${site.r}`);
    }
  }

  return def;
}
